import PhoneIcon from '@mui/icons-material/Phone';
import { Box } from '@mui/material';
import { GridColumns, GridSortModel } from '@mui/x-data-grid';
import axios from 'axios';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import relativeTime from 'dayjs/plugin/relativeTime';
import { SetStateAction } from 'react';
import {
  LuPhoneOff,
  LuPhone,
  LuPhoneMissed,
  LuMailWarning,
  LuMailCheck,
  LuMessageSquare,
  LuArrowDownLeft,
  LuArrowUpRight,
} from 'react-icons/lu';
import { ColumnSettings } from 'wisestep-api';
import {
  FieldOption,
  WsBadge,
  WsBadgeSize,
  WsBadgetype,
  WsColors,
  WsTypography,
} from 'wisestep-base';

import WsAvatar from '../../teams/components/WsAvatar';
import WsLink from '../../teams/components/WsLink';
import MessageCell from '../components/MessageCell';
import {
  CommType,
  TaggedPageType,
  ICommunicationLog,
  CommStatusKey,
  CommStatusType,
  SmsDetails,
} from '../types/CommunicationTypes';

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

export const formatCommunicationDate = (isoDate: string): string => {
  const date = dayjs(isoDate);
  if (date.isToday()) return `Today, ${date.format('HH:mm')}`;
  if (date.isYesterday()) return `Yesterday, ${date.format('HH:mm')}`;
  return date.format('DD MMM, ddd, HH:mm');
};

export const commTabs: { tabName: CommType }[] = [
  { tabName: 'All' },
  { tabName: 'Email' },
  { tabName: 'SMS' },
  { tabName: 'Call' },
];

export const taggedPageFieldMap: Record<TaggedPageType, string> = {
  RECRUITER: 'recruiterId',
  CANDIDATE: 'candidateId',
  JOB: 'jobId',
  TEAM: 'teamId',
};

export const defaultDateFilter = () => ({
  fromDate: dayjs().subtract(6, 'day').startOf('day').toISOString(),
  toDate: dayjs().endOf('day').toISOString(),
});

export const getCommStatusBadge = (status: CommStatusType, type: CommType) => {
  return (
    <WsBadge
      badgeSize={WsBadgeSize.LARGE}
      badgeType={communicationStatus[type]?.[status]?.badgeType}
      label={communicationStatus[type]?.[status]?.label ?? status}
      startIcon
      icon={communicationStatus[type]?.[status]?.icon as JSX.Element}
    />
  );
};

export const communicationStatus: CommStatusKey = {
  SMS: {
    SENT: {
      label: 'Delivered',
      badgeType: WsBadgetype.SUCCESS,
      icon: <LuMessageSquare size={20} />,
    },
    IN_QUEUE: {
      badgeType: WsBadgetype.FAILED,
      icon: <LuMessageSquare size={20} />,
    },
    RECEIVED: {
      label: 'Received',
      badgeType: WsBadgetype.SUCCESS,
      icon: <LuMessageSquare size={20} />,
    },
  },
  Email: {
    SENT: {
      label: 'Sent',
      badgeType: WsBadgetype.SUCCESS,
      icon: <LuMailCheck size={20} />,
    },
    SUCCESS: {
      label: 'Sent',
      badgeType: WsBadgetype.SUCCESS,
      icon: <LuMailCheck size={20} />,
    },
    FAILED_TO_SEND: {
      label: 'Failed To Send',
      badgeType: WsBadgetype.FAILED,
      icon: <LuMailWarning size={20} />,
    },
    duplicate: {
      label: 'Duplicate',
      badgeType: WsBadgetype.FAILED,
      icon: <LuMailWarning size={20} />,
    },
  },
  Call: {
    'Call Connected': {
      badgeType: WsBadgetype.SUCCESS,
      icon: <LuPhone size={20} />,
    },
    'No Answer': {
      label: 'No Response',
      badgeType: WsBadgetype.INACTIVE,
      icon: <LuPhoneMissed size={20} />,
    },
    'Call Cancelled': {
      badgeType: WsBadgetype.WARNING,
      icon: <PhoneIcon sx={{ fontSize: 20 }} />,
    },
    'Call Failed': {
      badgeType: WsBadgetype.FAILED,
      icon: <LuPhoneOff size={20} />,
    },
  },
};

export const defaultCommunicationLog = (): ICommunicationLog => ({
  candidateId: '',
  candidateName: '',
  createdOn: '',
  jobId: '',
  recruiterId: '',
  type: 'Email',
  status: 'SENT',
  jobTitle: '',
  contactFrom: '',
  contactTo: '',
  direction: 'INBOUND',
  teamId: '',
  teamName: '',
  sourceId: '',
  recruiterName: '',
  details: {
    subject: '',
    body: '',
    cc: [],
    bcc: [],
    isOpened: false,
    validate: false,
    isBounced: false,
    duration: '',
    callId: '',
    transcriptURL: '',
    creditConsumed: 0,
  },
});

export const communicationTypeOptions: { label: string; value: CommType }[] = [
  { label: 'All', value: 'All' },
  { label: 'Email', value: 'Email' },
  { label: 'SMS', value: 'SMS' },
  { label: 'Call', value: 'Call' },
];

export const userTypeOptions: { label: string; value: string }[] = [
  { label: 'Candidate', value: 'candidate' },
  { label: 'Client Contact', value: 'client_contact' },
  { label: 'Others', value: 'others' },
];

export const getAllCommunicationLogs = (commLogSearchPayload: any, config?: any) =>
  axios.post('/candidatesearch/api/all-communication-logs/search', commLogSearchPayload, config);

export const getCallStatusBadgeProps = (status: string) => {
  switch (status.toLowerCase()) {
    case 'answered':
      return {
        badgeType: WsBadgetype.SUCCESS,
        icon: <PhoneIcon sx={{ fontSize: 18 }} />,
      };
    case 'missed':
      return {
        badgeType: WsBadgetype.FAILED,
        icon: <PhoneIcon sx={{ fontSize: 18 }} />,
      };
    case 'declined':
      return {
        badgeType: WsBadgetype.WARNING,
        icon: <PhoneIcon sx={{ fontSize: 18 }} />,
      };
    default:
      return {
        badgeType: WsBadgetype.SUCCESS,
        icon: <PhoneIcon sx={{ fontSize: 18 }} />,
      };
  }
};

export const formatCallType = (type: string) => {
  return type.charAt(0).toUpperCase() + type.slice(1);
};

export const formatCallStatus = (status: string) => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const getMessageStatusBadgeProps = (status: string, creditCount?: number) => {
  switch (status) {
    case 'delivered':
      return {
        label: 'Delivered',
        badgeType: WsBadgetype.SUCCESS,
        backgroundColor: '#E0F5F4',
        textColor: '#008979',
      };
    case 'not_delivered':
      return {
        label: 'Not delivered',
        badgeType: WsBadgetype.FAILED,
        backgroundColor: '#FED8DD',
        textColor: '#FF2E49',
      };
    case 'credit_used':
      return {
        label: `Credit used- ${creditCount || 1}`,
        badgeType: WsBadgetype.WARNING,
        backgroundColor: '#EAEAF1',
        textColor: '#484D6D',
      };
    default:
      return {
        label: status,
        badgeType: WsBadgetype.WARNING,
        backgroundColor: '#EAEAF1',
        textColor: '#484D6D',
      };
  }
};

export const formatEmailStatus = (status: string) => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const formatJobTitle = (title: string, code?: string) => {
  return code ? `${title} (${code})` : title;
};

export const defaultSortModel = (): GridSortModel => [
  {
    field: 'createdOn',
    sort: 'desc',
  },
];

export const defaultCommColumnSettings = (tableKey: ColumnSettings): FieldOption[] => [
  { id: 'type', checked: true, required: true },
  { id: 'direction', checked: true },
  { id: 'app', checked: true },
  {
    id: 'contactFrom',
    checked:
      [
        ColumnSettings.JOB_SMS_LOGS,
        ColumnSettings.RECRUITER_SMS_LOGS,
        ColumnSettings.TEAM_SMS_LOGS,
        ColumnSettings.CANDIDATE_SMS_LOGS,
      ].includes(tableKey) == false,
  },
  { id: 'contactTo', checked: true },
  {
    id: 'candidateName',
    checked: [ColumnSettings.CANDIDATE_ALL_LOGS].includes(tableKey) == false,
  },
  {
    id: 'recruiterName',
    checked:
      [
        ColumnSettings.RECRUITER_ALL_LOGS,
        ColumnSettings.RECRUITER_SMS_LOGS,
        ColumnSettings.RECRUITER_CALL_LOGS,
      ].includes(tableKey) == false,
  },
  {
    id: 'jobTitle',
    checked:
      [
        ColumnSettings.JOB_ALL_LOGS,
        ColumnSettings.JOB_SMS_LOGS,
        ColumnSettings.JOB_CALL_LOGS,
      ].includes(tableKey) == false,
  },
  { id: 'createdOn', checked: true },
  { id: 'status', checked: true },
];

export const getTableKeysFor = (
  page: TaggedPageType
): Partial<Record<CommType, ColumnSettings>> => {
  switch (page) {
    case 'RECRUITER':
      return {
        SMS: ColumnSettings.RECRUITER_SMS_LOGS,
        Call: ColumnSettings.RECRUITER_CALL_LOGS,
        All: ColumnSettings.RECRUITER_ALL_LOGS,
      };
    case 'CANDIDATE':
      return {
        SMS: ColumnSettings.CANDIDATE_SMS_LOGS,
        Call: ColumnSettings.CANDIDATE_CALL_LOGS,
        All: ColumnSettings.CANDIDATE_ALL_LOGS,
      };
    case 'JOB':
      return {
        SMS: ColumnSettings.JOB_SMS_LOGS,
        Call: ColumnSettings.JOB_CALL_LOGS,
        All: ColumnSettings.JOB_ALL_LOGS,
      };
    case 'TEAM':
      return {
        SMS: ColumnSettings.TEAM_SMS_LOGS,
        Call: ColumnSettings.TEAM_CALL_LOGS,
        All: ColumnSettings.TEAM_ALL_LOGS,
      };
    default:
      throw new Error('Invalid page type');
  }
};

const formatSeconds = (totalSeconds: number): string => {
  if (!totalSeconds || totalSeconds <= 0) return '';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

export const getCommTableColumns = (
  activeTab: CommType,
  setActiveLog: (val: SetStateAction<ICommunicationLog | null>) => void
): GridColumns<ICommunicationLog> => [
  ...(activeTab !== 'SMS'
    ? [
        {
          field: 'type',
          headerName: 'Communication',
          minWidth: 90,
          flex: 1,
          sortable: false,
          renderCell: (params) => (
            <Box color={WsColors.TheBlues750} onClick={() => setActiveLog(params.row)}>
              <WsTypography color={WsColors.TheBlues750 as any} cursor="pointer">
                {params.value}
              </WsTypography>
            </Box>
          ),
        },
      ]
    : []),
  {
    field: 'direction',
    headerName: 'Direction',
    width: 90,
    sortable: false,
    renderCell: (params) => (
      <WsBadge
        badgeSize={WsBadgeSize.LARGE}
        badgeType={
          ['IN_BOUND', 'INBOUND'].includes(params.value)
            ? WsBadgetype.PENDING
            : WsBadgetype.INPROGRESS
        }
        label=""
        endIcon
        icon={
          ['IN_BOUND', 'INBOUND'].includes(params.value) ? (
            <LuArrowDownLeft size={24} />
          ) : (
            <LuArrowUpRight size={24} />
          )
        }
      />
    ),
  },
  ...((activeTab === 'Call'
    ? [
        {
          field: 'app',
          headerName: 'App',
          minWidth: 40,
          flex: 0.5,
          sortable: false,
          renderCell: (params) => <WsTypography>{params.value || 'Zoom'}</WsTypography>,
        },
      ]
    : []) as GridColumns<ICommunicationLog>),
  {
    field: 'contactFrom',
    headerName: 'From',
    minWidth: 150,
    flex: 1.5,
    sortable: false,
    renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
  },
  {
    field: 'contactTo',
    headerName: 'To',
    minWidth: 180,
    flex: 1.5,
    sortable: false,
    renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
  },
  {
    field: 'candidateName',
    headerName: 'Candidate',
    minWidth: 180,
    flex: 1,
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" alignItems="center" gap={1}>
        <WsAvatar />
        <WsLink to={`/candidate-layout/submissions?id=${params.row.candidateId}`}>
          <WsTypography>{params.value}</WsTypography>
        </WsLink>
      </Box>
    ),
  },
  {
    field: 'recruiterName',
    headerName: 'User',
    minWidth: 180,
    flex: 1,
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" alignItems="center" gap={1}>
        <WsAvatar />
        <WsLink
          to={`/users/details-page?email=${params.row.recruiterId}&isLink=true&isUserAnalytics=true`}
        >
          <WsTypography>{params.value}</WsTypography>
        </WsLink>
      </Box>
    ),
  },
  ...((activeTab === 'SMS'
    ? [
        {
          field: 'credit',
          headerName: 'Credit Consumed',
          width: 60,
          sortable: false,
          renderCell: (params) => (
            <WsTypography>{(params.row.details as SmsDetails).creditConsumed}</WsTypography>
          ),
        },
        {
          field: 'message',
          headerName: 'Message',
          minWidth: 180,
          flex: 1.5,
          sortable: false,
          renderCell: (params) => (
            <MessageCell
              text={(params.row.details as SmsDetails).body}
              onShowMore={() => setActiveLog(params.row)}
            />
          ),
        },
      ]
    : []) as GridColumns<ICommunicationLog>),
  {
    field: 'jobTitle',
    headerName: 'Job Title',
    minWidth: 200,
    flex: 1,
    sortable: false,
    renderCell: (params) => (
      <WsLink to={`/jobs-crm/${params.row.jobId}/false/0/pipeline`}>
        <WsTypography>{params.value}</WsTypography>
      </WsLink>
    ),
  },
  {
    field: 'createdOn',
    headerName: 'Timestamp',
    minWidth: 150,
    flex: 1,
    sortable: true,
    renderCell: (params) => <WsTypography>{formatCommunicationDate(params.value)}</WsTypography>,
  },
  ...((activeTab === 'Call'
    ? [
        {
          field: 'duration',
          headerName: 'Duration',
          minWidth: 100,
          flex: 1,
          sortable: false,
          renderCell: (params) => <WsTypography>{formatSeconds(params.value)}</WsTypography>,
        },
      ]
    : []) as GridColumns<ICommunicationLog>),
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 150,
    flex: 1.5,
    sortable: false,
    renderCell: (params) => getCommStatusBadge(params.row.status, params.row.type),
  },
];
